import {
  AccountBookOutlined,
  AlignLeftOutlined,
  ShoppingCartOutlined,
  BookOutlined,
  BankOutlined,
  ScheduleOutlined,
  UserOutlined,
  EditOutlined,
  LineChartOutlined,
  CheckSquareFilled,
  BookFilled,
  SettingFilled,
  ClockCircleFilled,
  FileTextFilled,
  FormOutlined,
  UnorderedListOutlined,
  CloudSyncOutlined,
} from '@ant-design/icons';
import { MergeOutlined } from '@mui/icons-material';
import { isPathAllowedForRole } from 'utils/authorization';
import { isMobile } from 'react-device-detect';
import AirplaneModeActive from '../components/icons/AirplaneModeActive.svg';
import Airport from '../components/icons/Airport.svg';
import FlightTakeoff from '../components/icons/FlightTakeoff.svg';
import People from '../components/icons/People.svg';
import ScheduleIcon from '../components/icons/ScheduleIcon.svg';
import api from './api';

const SUPPORT_EMAIL_PATTERN = /^support\+[^@]+@airtrail\.co$/i;
const isSupportAccount = email => !!email && SUPPORT_EMAIL_PATTERN.test(email.trim());

const getMenuData = async (roles, companySettings, email) => {
  const menu = [];
  let data = {};
  try {
    const response = await api.get('/v1/dashboard/menu');
    if (response.data && response.data.Data) {
      data = response.data.Data;
    }
  } catch (e) {
    console.log(e);
  }

  const checkPath = path => {
    return isPathAllowedForRole(roles, path, companySettings);
  };

  const { ShowAerochexLogs, ShowPilot, CrewSchedulingOnly } = data;
  const sales = {
    title: 'Sales',
    key: 'sales',
    iconComponent: ShoppingCartOutlined,
  };

  sales.children = [
    checkPath('/data/organization/sales-dashboard') && {
      title: 'Dashboard',
      key: 'salesDashboard',
      url: '/data/organization/sales-dashboard',
    },
    checkPath('/data/organization/minimums-dashboard') && {
      title: 'Average Mins',
      key: 'minimumsDashboard',
      url: '/data/organization/minimums-dashboard',
    },
    checkPath('/data/organization/company-clients') && {
      title: 'Clients',
      key: 'companyClients',
      url: '/data/organization/company-clients',
    },
    checkPath('/data/organization/invoices') && {
      title: 'Invoices',
      key: 'invoices',
      url: '/data/organization/invoices',
    },
    checkPath('/data/organization/air-bills') && {
      title: 'Tickets',
      key: 'air-bills',
      url: '/data/organization/air-bills',
    },
    checkPath('/data/organization/groups') && {
      title: 'Bases',
      key: 'groups',
      url: '/data/organization/groups',
    },
    checkPath('/data/organization/bookings') && {
      title: 'Bookings',
      key: 'bookings',
      url: '/data/organization/bookings',
    },
  ];

  if (companySettings?.SalesModule) {
    menu.push(sales, { divider: true });
  }

  const organization = {
    title: 'Organization',
    key: 'organization',
    iconComponent: BankOutlined,
  };

  organization.children = [
    checkPath('/data/organization/metrics-dashboard') && {
      title: 'Dashboard',
      key: 'dashboard',
      url: '/data/organization/metrics-dashboard',
      iconComponent: LineChartOutlined,
    },

    checkPath('/data/organization/logs') && {
      title: 'Logs',
      key: 'logs',
      url: '/data/organization/logs',
      iconComponent: BookOutlined,
    },
    checkPath('/data/organization/aircraft') && {
      title: 'Aircraft',
      key: 'aircraft',
      url: '/data/organization/aircraft',
      iconComponent: AirplaneModeActive,
    },
    checkPath('/data/organization/fleet-overview') && {
      title: companySettings?.CompanyFieldNameMappings?.FleetOverview || 'Fleet Overview',
      key: 'fleet-overview',
      url: '/data/organization/fleet-overview',
      iconComponent: AirplaneModeActive,
    },
    checkPath('/data/organization/parts') && {
      title: companySettings?.CompanyFieldNameMappings?.MajorAssembliesPlural || 'Parts',
      key: 'parts',
      url: '/data/organization/parts',
      iconComponent: MergeOutlined,
    },
    checkPath('/data/organization/duty-times') && {
      title: 'Duty Times',
      key: 'duty-times',
      url: '/data/organization/duty-times',
      iconComponent: ScheduleOutlined,
    },
    checkPath('/data/organization/users') && {
      title: 'Users',
      key: 'users',
      url: '/data/organization/users',
      iconComponent: People,
    },
    checkPath('/data/organization/crew-compliance') && {
      title: 'Crew Compliance',
      key: 'crew-compliance',
      url: '/data/organization/crew-compliance/duty-times',
      iconComponent: ScheduleOutlined,
    },
    checkPath('/data/organization/crew-scheduling') && {
      title: companySettings?.MaintenanceSchedule ? 'Schedule' : 'Crew Schedule',
      key: 'crew-scheduling',
      url: '/data/organization/crew-scheduling',
      iconComponent: ScheduleIcon,
    },
    ((isMobile &&
      companySettings?.ShowDesktopScheduleOnMobile &&
      !companySettings?.PublishedSchedule) ||
      !isMobile) &&
      checkPath('/data/organization/crew-scheduling-v3') && {
        title: companySettings?.MaintenanceSchedule ? 'Schedule' : 'Crew Schedule',
        key: 'crew-scheduling-v3',
        url: '/data/organization/crew-scheduling-v3',
        iconComponent: ScheduleIcon,
      },
    isMobile &&
      !companySettings?.PublishedSchedule &&
      checkPath('/data/organization/crew-scheduling-mobile') && {
        title: 'Crew Schedule (Mobile)',
        key: 'crew-scheduling-mobile',
        url: '/data/organization/crew-scheduling-mobile',
        iconComponent: ScheduleIcon,
      },
    checkPath('/data/organization/regulations') && {
      title: 'Regulations',
      key: 'regulations',
      url: '/data/organization/regulations',
      iconComponent: CheckSquareFilled,
    },
    checkPath('/data/organization/reports') && {
      title: 'Reports',
      key: 'reports',
      url: '/data/organization/reports',
      iconComponent: BookFilled,
    },
    checkPath('/data/organization/settings') && {
      title: 'Settings',
      key: 'settings',
      url: '/data/organization/settings',
      iconComponent: SettingFilled,
    },
    checkPath('/data/organization/flight-watch') && {
      title: 'Flight Watch',
      key: 'flightWatch',
      url: '/data/organization/flight-watch',
      iconComponent: FlightTakeoff,
    },
    checkPath('/data/organization/pilot-status') && {
      title: 'Pilot Status',
      key: 'pilotStatus',
      url: '/data/organization/pilot-status',
      iconComponent: ClockCircleFilled,
    },
    checkPath('/data/organization/company-library') && {
      title: 'Company Library',
      key: 'companyLibrary',
      url: '/data/organization/company-library',
      iconComponent: FileTextFilled,
    },
    checkPath('/data/organization/custom-forms') && {
      title: 'Custom Forms',
      key: 'customForms',
      url: '/data/organization/custom-forms',
      iconComponent: FormOutlined,
    },
    checkPath('/data/organization/tracked-items') && {
      title: 'Tracked Items',
      key: 'trainingItems',
      url: '/data/organization/tracked-items',
      iconComponent: UnorderedListOutlined,
    },
  ];

  if (companySettings?.PublishedSchedule) {
    organization.children.push(
      checkPath('/data/organization/published-schedule') && {
        title: 'Published Schedule',
        key: 'publishedSchedule',
        url: '/data/organization/published-schedule',
        iconComponent: AlignLeftOutlined,
      },
    );
  }

  if (isSupportAccount(email)) {
    organization.children.push({
      title: 'Outbox Monitor',
      key: 'outbox-monitor',
      url: '/data/organization/outbox-monitor',
      iconComponent: CloudSyncOutlined,
    });
  }

  menu.push(organization, { divider: true });

  if (companySettings?.ManifestOptions) {
    const opts = companySettings.ManifestOptions;
    const children = [
      {
        title: 'Schedule',
        key: 'manifest-calendar',
        url: '/data/organization/manifest/calendar',
        iconComponent: ScheduleOutlined,
      },
    ];

    if (opts.showShiftRequests !== false) {
      children.push({
        title: 'Shift Requests',
        key: 'manifest-shift-requests',
        url: '/data/organization/manifest/shift-requests',
        iconComponent: ScheduleOutlined,
      });
    }

    if (opts.showBookings) {
      children.push({
        title: 'Bookings',
        key: 'manifest-bookings',
        url: `/data/organization/manifest/tables/${opts.bookingsSlug || 'keewatin-booking-request'}`,
        iconComponent: ScheduleOutlined,
      });
    }

    if (opts.showClients) {
      children.push({
        title: 'Clients',
        key: 'manifest-clients',
        url: '/data/organization/manifest/clients',
        iconComponent: People,
      });
    }

    if (opts.showAirports) {
      children.push({
        title: 'Airports',
        key: 'manifest-airports',
        url: '/data/organization/manifest/airports',
        iconComponent: Airport,
      });
    }

    if (opts.showRateSheets) {
      children.push({
        title: 'Rate Sheets',
        key: 'manifest-rate-sheets',
        url: '/data/organization/manifest/rate-sheets',
        iconComponent: AccountBookOutlined,
      });
    }

    if (opts.showPilotSummary !== false) {
      children.push({
        title: 'Pilot Summary',
        key: 'manifest-pilot-summary',
        url: '/data/organization/manifest/pilot-summary',
        iconComponent: ClockCircleFilled,
      });
    }

    children.push({
      title: 'Users',
      key: 'manifest-users',
      url: '/data/organization/manifest/users',
      iconComponent: People,
    });

    const manifest = {
      title: 'Manifest',
      key: 'manifest',
      iconComponent: ScheduleIcon,
      children,
    };
    menu.push(manifest, { divider: true });
  }

  if (ShowPilot) {
    menu.push({
      title: 'My Profile',
      key: 'me',
      iconComponent: UserOutlined,
      children: [
        checkPath('/profile/manage') && {
          title: 'Manage Profile',
          key: 'me/manage',
          url: '/profile/manage',
          iconComponent: EditOutlined,
        },
        checkPath('/profile/duty') && {
          title: 'Flight And Duty',
          key: 'me/duty',
          url: '/profile/duty',
          iconComponent: ScheduleOutlined,
        },
      ],
    });
  }

  if (ShowAerochexLogs) {
    organization.children.push(
      checkPath('/data/organization/aerochex') && {
        title: 'Aerochex',
        key: 'aerochex',
        url: '/data/organization/aerochex',
      },
    );
  }

  menu.push(
    checkPath('/data/airports') && {
      title: 'Airports',
      key: 'airports',
      url: '/data/airports',
      iconComponent: Airport,
    },
  );

  // menu.push(
  //   {
  //     title: 'Settings',
  //     key: 'settings',
  //     url: '/settings',
  //     iconComponent: SettingOutlined,
  //     children :[
  //       {
  //         title: 'Timezone',
  //         key: 'settings_timezone',
  //         url: '/settings/timezone',
  //         iconComponent: ClockCircleOutlined,
  //       }
  //     ]
  //   },
  // );

  return menu
    .filter(x => x)
    .map(menuItem => ({ ...menuItem, children: menuItem.children?.filter(x => x) }))
    .filter(item => item?.children?.length > 0); // remove null or undefined children and items with no children
};

export default getMenuData;
